// src/pages/SettingsPage.js
// Full accessibility and language settings

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings, LANGUAGES } from '../lib/SettingsContext';

const SettingsPage = () => {
  const navigate = useNavigate();
  const {
    language, setLanguage,
    textSize, setTextSize,
    highContrast, setHighContrast,
    t,
  } = useSettings();

  const TEXT_SIZES = [
    { value: 'normal', label: 'Normal', size: 15 },
    { value: 'large',  label: 'Large',  size: 19 },
    { value: 'xlarge', label: 'Extra large', size: 22 },
  ];

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh', padding: '52px 20px 40px' }}>

      {/* Header */}
      <button
        onClick={() => navigate(-1)}
        style={s.back}
        aria-label="Go back"
      >
        <div style={s.backCircle} aria-hidden="true">←</div>
        <span style={s.backLbl}>Back</span>
      </button>

      <h1 style={s.h1}>{t('settings')}</h1>

      {/* ── LANGUAGE ── */}
      <Section title={t('language')} icon="🌐">
        <div style={s.optionGrid}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              style={{
                ...s.langBtn,
                background: language === lang.code ? 'var(--sage-dark)' : 'var(--white)',
                color: language === lang.code ? 'white' : 'var(--ink)',
                border: `2px solid ${language === lang.code ? 'var(--sage-dark)' : 'var(--border)'}`,
              }}
              onClick={() => setLanguage(lang.code)}
              aria-pressed={language === lang.code}
              aria-label={`Set language to ${lang.label}`}
            >
              <span style={{ fontSize: 20 }}>{lang.flag}</span>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{lang.label}</span>
            </button>
          ))}
        </div>
      </Section>

      {/* ── TEXT SIZE ── */}
      <Section title={t('textSize')} icon="🔡">
        <div style={s.sizeRow}>
          {TEXT_SIZES.map(sz => (
            <button
              key={sz.value}
              style={{
                ...s.sizeBtn,
                background: textSize === sz.value ? 'var(--sage-dark)' : 'var(--white)',
                color: textSize === sz.value ? 'white' : 'var(--ink)',
                border: `2px solid ${textSize === sz.value ? 'var(--sage-dark)' : 'var(--border)'}`,
                fontSize: sz.size,
              }}
              onClick={() => setTextSize(sz.value)}
              aria-pressed={textSize === sz.value}
              aria-label={`Set text size to ${sz.label}`}
            >
              A
            </button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500, marginTop: 8 }}>
          {TEXT_SIZES.find(s => s.value === textSize)?.label} text
        </div>
      </Section>

      {/* ── HIGH CONTRAST ── */}
      <Section title={t('highContrast')} icon="◑">
        <div style={s.toggleRow}>
          <div>
            <div style={s.toggleTitle}>High contrast mode</div>
            <div style={s.toggleSub}>
              Darker colours and stronger borders — easier to read in bright light or for low vision
            </div>
          </div>
          <button
            style={{
              ...s.toggle,
              background: highContrast ? 'var(--sage-deep)' : 'var(--border)',
            }}
            onClick={() => setHighContrast(!highContrast)}
            role="switch"
            aria-checked={highContrast}
            aria-label="Toggle high contrast mode"
          >
            <div style={{
              ...s.toggleThumb,
              transform: highContrast ? 'translateX(22px)' : 'translateX(2px)',
            }} />
          </button>
        </div>
      </Section>

      {/* ── ACCESSIBILITY INFO ── */}
      <Section title="Accessibility" icon="♿">
        <div style={s.infoBox}>
          <div style={s.infoItem}>
            <span style={s.infoIcon}>♿</span>
            <div>
              <div style={s.infoTitle}>Accessibility at Gather</div>
              <div style={s.infoSub}>
                <button onClick={() => navigate('/accessibility')} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--sage-deep)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Nunito' }}>
                  Why it matters, and tips for local businesses →
                </button>
              </div>
            </div>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoIcon}>📱</span>
            <div>
              <div style={s.infoTitle}>Screen reader compatible</div>
              <div style={s.infoSub}>Works with VoiceOver (iPhone) and TalkBack (Android)</div>
            </div>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoIcon}>📶</span>
            <div>
              <div style={s.infoTitle}>Works offline</div>
              <div style={s.infoSub}>Events and services load even without internet</div>
            </div>
          </div>
          <div style={s.infoItem}>
            <span style={s.infoIcon}>✉️</span>
            <div>
              <div style={s.infoTitle}>Accessibility feedback</div>
              <div style={s.infoSub}>
                <a href="mailto:hello@gatheryourcommunity.com?subject=Accessibility feedback" style={{ color: 'var(--sage-deep)', fontWeight: 700 }}>
                  Tell us how we can improve →
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Reset all settings */}
      <button
        style={s.resetBtn}
        onClick={() => {
          setLanguage('en');
          setTextSize('normal');
          setHighContrast(false);
        }}
      >
        Reset all settings to default
      </button>

    </div>
  );
};

// Section wrapper
const Section = ({ title, icon, children }) => (
  <div style={sectionS.wrap}>
    <div style={sectionS.header}>
      <span style={{ fontSize: 18 }} aria-hidden="true">{icon}</span>
      <span style={sectionS.title}>{title}</span>
    </div>
    <div style={sectionS.body}>{children}</div>
  </div>
);

const sectionS = {
  wrap: { background: 'var(--white)', borderRadius: 18, marginBottom: 14, overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  header: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px 10px', borderBottom: '1px solid var(--border)' },
  title: { fontSize: 15, fontWeight: 800, color: 'var(--ink)' },
  body: { padding: '14px 18px 18px' },
};

const s = {
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, minHeight: 44 },
  backCircle: { width: 36, height: 36, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, boxShadow: 'var(--shadow-sm)', color: 'var(--sage-dark)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 28, fontWeight: 800, color: 'var(--ink)', marginBottom: 20, letterSpacing: '-0.3px' },

  optionGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  langBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    padding: '14px 10px', borderRadius: 14,
    cursor: 'pointer', transition: 'all 0.15s',
    fontFamily: 'Nunito, sans-serif', minHeight: 70,
  },

  sizeRow: { display: 'flex', gap: 10 },
  sizeBtn: {
    flex: 1, padding: '14px 10px', borderRadius: 14,
    cursor: 'pointer', transition: 'all 0.15s',
    fontFamily: 'Nunito, sans-serif', fontWeight: 700, minHeight: 54,
  },

  toggleRow: { display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'space-between' },
  toggleTitle: { fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 4 },
  toggleSub: { fontSize: 12, color: 'var(--muted)', fontWeight: 500, lineHeight: 1.45 },
  toggle: {
    width: 48, height: 28, borderRadius: 14,
    border: 'none', cursor: 'pointer', flexShrink: 0,
    position: 'relative', transition: 'background 0.2s',
    minHeight: 28, // override 44px for inline toggle
  },
  toggleThumb: {
    position: 'absolute', top: 3,
    width: 22, height: 22,
    background: 'white', borderRadius: '50%',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
  },

  infoBox: { display: 'flex', flexDirection: 'column', gap: 14 },
  infoItem: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  infoIcon: { fontSize: 22, flexShrink: 0, marginTop: 1 },
  infoTitle: { fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 },
  infoSub: { fontSize: 12, color: 'var(--muted)', fontWeight: 500 },

  resetBtn: {
    width: '100%', background: 'none', border: '1.5px solid var(--border)',
    borderRadius: 14, padding: 14, fontSize: 13, fontWeight: 600,
    color: 'var(--muted)', fontFamily: 'Nunito, sans-serif', cursor: 'pointer',
    marginTop: 8,
  },
};

export default SettingsPage;
